import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const LINKS = [
  { label: "Story", href: "#story" },
  { label: "Speaking", href: "#speaking" },
  { label: "Media", href: "#media" },
  { label: "Books", href: "#books" },
  { label: "Podcast", href: "#podcast" },
  { label: "The App", href: "#app" },
];

export const scrollToId = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
        scrolled ? "border-[#6B5D4F]/10 bg-[#FDFAF4]/80 backdrop-blur-xl" : "border-transparent bg-[#FDFAF4]/40 backdrop-blur-md"
      }`}
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.76, 0, 0.24, 1] }}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
          className="group flex items-baseline gap-3"
        >
          <span className="font-display text-3xl font-medium tracking-wide text-[#6B5D4F]">SG</span>
          <span className="hidden font-body text-[0.6rem] font-medium uppercase tracking-[0.45em] text-[#8B7D6B] sm:block">
            Susan Grau
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => scrollToId(l.href)}
              className="gold-underline pb-1 font-body text-[0.7rem] font-medium uppercase tracking-[0.25em] text-[#8B7D6B] transition-colors duration-300 hover:text-[#6B5D4F]"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="nav-book-susan-btn"
            onClick={() => scrollToId("#press")}
            className="group relative overflow-hidden border border-[#6B5D4F] px-6 py-2.5 font-body text-[0.65rem] font-medium uppercase tracking-[0.3em] text-[#6B5D4F]"
          >
            <span className="absolute inset-0 -translate-x-full bg-[#6B5D4F] transition-transform duration-500 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:translate-x-0" />
            <span className="relative transition-colors duration-500 group-hover:text-[#FDFAF4]">Book Susan</span>
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            <span className={`h-px w-6 bg-[#6B5D4F] transition-transform duration-300 ${open ? "translate-y-[3.5px] rotate-45" : ""}`} />
            <span className={`h-px w-6 bg-[#6B5D4F] transition-transform duration-300 ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      {open && (
        <motion.nav
          data-testid="nav-mobile-menu"
          className="border-t border-[#6B5D4F]/10 bg-[#FDFAF4]/95 px-6 py-6 backdrop-blur-xl lg:hidden"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => { setOpen(false); scrollToId(l.href); }}
                className="text-left font-display text-2xl text-[#6B5D4F]"
              >
                {l.label}
              </button>
            ))}
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
}
